import { useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AirtableIcon } from '@/components/ui/icons/airtable-icon';
import { SixSenseIcon } from '@/components/ui/icons/sixsense-icon';
import {
  GoogleDocsIcon,
  GoogleSheetsIcon,
} from '@/components/ui/icons/google-product-icons';
import {
  FathomIcon,
  FirefliesIcon,
  GongIcon,
  NotionIcon,
} from '@/components/ui/icons/meeting-notes-icons';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { rememberConnectingProvider } from '@/lib/workspace-oauth-callback';
import { GoogleCalendarIcon } from './google-calendar-icon.component';
import { GongConnectDialog } from './gong-connect-dialog.component';
import {
  ConnectButton,
  ConnectedPill,
  DESTRUCTIVE_ITEM,
  IntegrationCard,
  IntegrationConnectionDialog,
  ManageMenu,
  ReconnectButton,
  StatusPill,
  UpgradeLink,
} from './integration-card.component';
import {
  useConnectWorkspaceMutation,
  useDisconnectWorkspaceMutation,
  useGetWorkspaceStatusQuery,
  type WorkspaceProvider,
} from '@/store/features/workspace-connections.feature';
import { BRAND } from '@/lib/brand';

export const WORKSPACE_PROVIDERS = {
  google_calendar: {
    section: 'workspace',
    name: 'Google Calendar',
    vendor: 'Google',
    Icon: GoogleCalendarIcon,
    blurb:
      'Read calendar events for meeting briefs and follow-ups in this workspace.',
    disconnectNote:
      'Plays stop reading your Google Calendar until you reconnect.',
    keepsNote: () =>
      `Your events stay in Google Calendar. ${BRAND.name} cannot change them.`,
  },
  google_sheets: {
    section: 'workspace',
    name: 'Google Sheets',
    vendor: 'Google',
    Icon: GoogleSheetsIcon,
    blurb:
      'Export lists to Sheets, import a sheet into a list, and let plays add rows.',
    disconnectNote: 'Your exports to Google Sheets stop until you reconnect.',
    keepsNote: (account: string) =>
      `Files already created stay in ${account}’s Drive.`,
  },
  google_docs: {
    section: 'workspace',
    name: 'Google Docs',
    vendor: 'Google',
    Icon: GoogleDocsIcon,
    blurb: 'Let Clu save briefs and write-ups as Google Docs.',
    disconnectNote: 'Clu can’t create Google Docs for you until you reconnect.',
    keepsNote: (account: string) =>
      `Files already created stay in ${account}’s Drive.`,
  },
  airtable: {
    section: 'workspace',
    name: 'Airtable',
    vendor: 'Airtable',
    Icon: AirtableIcon,
    blurb:
      'Let Clu export lists to new tables in the Airtable bases you choose.',
    disconnectNote: 'Clu can’t export to Airtable for you until you reconnect.',
    keepsNote: (account: string) =>
      `Tables already exported stay in ${account}’s Airtable.`,
  },
  fireflies: {
    section: 'knowledge',
    name: 'Fireflies',
    vendor: 'Fireflies',
    Icon: FirefliesIcon,
    blurb:
      'Sync your team’s meeting summaries into Knowledge, so Clu can answer what was discussed with an account.',
    disconnectNote:
      'Synced meetings are removed from Knowledge and new ones stop syncing.',
    keepsNote: () => 'Recordings and notes stay in Fireflies.',
  },
  fathom: {
    section: 'knowledge',
    name: 'Fathom',
    vendor: 'Fathom',
    Icon: FathomIcon,
    blurb:
      'Sync your Fathom meeting summaries and action items into Knowledge, so Clu can answer what was discussed with an account.',
    disconnectNote:
      'Synced meetings are removed from Knowledge and new ones stop syncing.',
    // Fathom has no revoke API: the grant lives on until it's removed there.
    keepsNote: () =>
      `Recordings and notes stay in Fathom. To fully revoke access, also remove ${BRAND.name} from your Fathom settings.`,
  },
  gong: {
    section: 'knowledge',
    name: 'Gong',
    vendor: 'Gong',
    Icon: GongIcon,
    blurb:
      'Sync your team’s Gong call briefs, key points and next steps into Knowledge, so Clu can answer what was discussed with an account. A Gong technical admin approves the connection.',
    disconnectNote:
      'Synced calls are removed from Knowledge and new ones stop syncing.',
    // Gong has no revoke API: the grant or access key works until an admin
    // removes it.
    keepsNote: () =>
      `Recordings and notes stay in Gong. To fully revoke access, a Gong admin also removes ${BRAND.name} from Gong’s integrations, or deletes the access key in Gong’s API settings.`,
  },
  notion: {
    section: 'knowledge',
    name: 'Notion',
    vendor: 'Notion',
    Icon: NotionIcon,
    blurb:
      'Sync the Notion pages you choose into Knowledge, so Clu and your agents can use them.',
    disconnectNote: 'Synced pages are removed from Knowledge and stop syncing.',
    keepsNote: () => 'Your pages stay in Notion.',
  },
  sixsense_mcp: {
    section: 'sales',
    name: '6sense for Clu',
    vendor: '6sense',
    Icon: SixSenseIcon,
    blurb:
      'Let Clu read your 6sense: web visits, keywords, account insights. Each person connects their own 6sense.',
    disconnectNote: 'Clu can’t read your 6sense until you reconnect.',
    keepsNote: () => 'Your data stays in 6sense.',
  },
} as const;

/**
 * One workspace connection. Connect navigates to the provider's consent
 * screen; it returns to /integrations/{google,fireflies,fathom,gong,notion,sixsense-mcp}/callback (Google's is
 * shared by both products, which is why the product is remembered before
 * leaving), and that page lands on /integrations with the outcome toasted.
 * An AppSumo-only org can't connect (the backend fences it), so its card,
 * one set up before the license, offers the Pro pill in place of Reconnect
 * and keeps Disconnect.
 */
export function WorkspaceConnectionCard({
  provider,
  organizationId,
}: {
  provider: WorkspaceProvider;
  organizationId: string;
}) {
  const { isAppSumoOnly } = useFeatureAccess();
  const meta = WORKSPACE_PROVIDERS[provider];
  const args = { provider, organizationId };
  const { currentData: status, isLoading } = useGetWorkspaceStatusQuery(args);
  const [connect, { isLoading: isConnecting }] = useConnectWorkspaceMutation();
  const [disconnect, { isLoading: isDisconnecting }] =
    useDisconnectWorkspaceMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [gongOpen, setGongOpen] = useState(false);
  const [detailsOrganization, setDetailsOrganization] = useState<string | null>(
    null
  );

  const handleConnect = async () => {
    // Gong offers OAuth or an access key, so it asks which first.
    if (provider === 'gong') {
      setDetailsOrganization(null);
      setGongOpen(true);
      return;
    }
    await startOAuth();
  };

  const startOAuth = async () => {
    try {
      const { authorizeUrl } = await connect(args).unwrap();
      rememberConnectingProvider(provider);
      window.location.href = authorizeUrl;
    } catch (err) {
      const e = err as {
        status?: number;
        data?: { detail?: string | { message?: string } };
      };
      const detail =
        typeof e?.data?.detail === 'string'
          ? e.data.detail
          : e?.data?.detail?.message;
      toast.error(
        e?.status === 503
          ? `${meta.name} isn’t set up yet — check back soon.`
          : (detail ?? `Couldn’t start the ${meta.name} connection.`)
      );
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect(args).unwrap();
      setConfirmOpen(false);
      toast.success(`${meta.name} disconnected.`);
    } catch {
      toast.error(`Couldn’t disconnect ${meta.name}. Try again.`);
    }
  };

  const connected = !!status?.connected;
  const dropped = !connected && !!status?.reconnectRequired;
  const manage = (
    <ManageMenu name={meta.name}>
      {status?.canManage ? (
        <DropdownMenuItem
          className={DESTRUCTIVE_ITEM}
          disabled={isDisconnecting}
          onSelect={() => setConfirmOpen(true)}
        >
          Disconnect
        </DropdownMenuItem>
      ) : (
        <DropdownMenuItem disabled>Managed by a teammate</DropdownMenuItem>
      )}
    </ManageMenu>
  );

  return (
    <>
      <IntegrationCard
        id={provider}
        logo={<meta.Icon className='size-5' />}
        name={meta.name}
        onConfigure={
          !isLoading && (connected || dropped)
            ? () => setDetailsOrganization(organizationId)
            : undefined
        }
        badge={
          dropped && <StatusPill tone='warning'>Reconnect needed</StatusPill>
        }
        description={
          isLoading ? (
            <Skeleton className='mt-1 h-3 w-48' />
          ) : connected ? (
            `Connected as ${status?.accountLabel || `your ${meta.vendor} account`}${status?.canManage ? '' : ' · managed by a teammate'}`
          ) : dropped ? (
            `${meta.vendor} stopped accepting the connection — reconnect to keep using ${meta.name}.`
          ) : (
            meta.blurb
          )
        }
        action={
          connected ? (
            <>
              <ConnectedPill />
              {manage}
            </>
          ) : dropped ? (
            <>
              {isAppSumoOnly ? (
                <UpgradeLink name={meta.name} />
              ) : (
                <ReconnectButton
                  name={meta.name}
                  onClick={handleConnect}
                  busy={isConnecting}
                />
              )}
              {manage}
            </>
          ) : (
            <ConnectButton
              name={meta.name}
              onClick={handleConnect}
              disabled={isLoading}
              busy={isConnecting}
            />
          )
        }
      />

      <IntegrationConnectionDialog
        name={meta.name}
        open={detailsOrganization === organizationId && (connected || dropped)}
        onOpenChange={(open) => {
          if (!open) setDetailsOrganization(null);
        }}
        description={
          connected
            ? `Connected as ${status?.accountLabel || `your ${meta.vendor} account`}. ${meta.blurb}`
            : `${meta.vendor} stopped accepting this connection. Reconnect to keep using ${meta.name}.`
        }
      >
        {status?.canManage ? (
          <>
            {!isAppSumoOnly && (
              <Button
                variant='outline'
                disabled={isConnecting}
                onClick={handleConnect}
              >
                {connected ? 'Reauthorize' : 'Reconnect'}
              </Button>
            )}
            <Button
              variant='destructive'
              disabled={isDisconnecting}
              onClick={() => {
                setDetailsOrganization(null);
                setConfirmOpen(true);
              }}
            >
              Disconnect
            </Button>
          </>
        ) : (
          <p className='text-sm text-muted-foreground'>
            Managed by a teammate. Ask them to update this connection.
          </p>
        )}
      </IntegrationConnectionDialog>

      {provider === 'gong' && (
        <GongConnectDialog
          organizationId={organizationId}
          open={gongOpen}
          onOpenChange={setGongOpen}
          onConnectOAuth={startOAuth}
          connectingOAuth={isConnecting}
        />
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {meta.name}</AlertDialogTitle>
            <AlertDialogDescription>
              {meta.disconnectNote}{' '}
              {meta.keepsNote(status?.accountLabel || 'the connected account')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant='destructive' onClick={handleDisconnect}>
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
